/**
 * `numbers` → **Settings** (spec M1-B5 §6.2, M2 §5.4).
 *
 * The whole of monday's number formatting lives in `settings_str.unit`:
 * `{symbol, custom_unit, direction, precision}`. `symbol: "custom"` is what makes
 * `custom_unit` meaningful, `direction` puts the symbol before or after, and `precision: -1`
 * is monday's "as typed" (no rounding) — which is why the field is a select and not a
 * number input with a 0 default.
 */
import type { NumbersSettings } from '@elizmonday/shared';
import { useState } from 'react';
import type { ColumnSettingsProps } from '../types';

/** The symbols monday offers, plus `custom`. `null` is "no unit". */
const SYMBOLS = ['', '$', '€', '£', '₪', '¥', '%', 'custom'] as const;

export function NumbersSettingsDialog({
  settings,
  onCommit,
  onCancel,
}: ColumnSettingsProps<NumbersSettings>) {
  // Round 10i: `column.settings` is `null` on a column monday never configured.
  const [draft, setDraft] = useState<NumbersSettings>(() => ({ ...(settings ?? {}) }));
  const unit = draft.unit ?? {};
  const patchUnit = (next: Partial<NonNullable<NumbersSettings['unit']>>) =>
    setDraft((current) => ({ ...current, unit: { ...(current.unit ?? {}), ...next } }));

  return (
    <form
      className="ez-column-settings__form"
      data-testid="numbers-settings"
      onSubmit={(event) => {
        event.preventDefault();
        onCommit(draft);
      }}
    >
      <h2 className="ez-column-settings__title">Number settings</h2>

      <label className="ez-column-settings__field">
        Unit
        <select
          data-testid="numbers-symbol"
          value={unit.symbol ?? ''}
          onChange={(event) => patchUnit({ symbol: event.target.value === '' ? null : event.target.value })}
        >
          {SYMBOLS.map((symbol) => (
            <option key={symbol || 'none'} value={symbol}>
              {symbol === '' ? 'None' : symbol === 'custom' ? 'Custom…' : symbol}
            </option>
          ))}
        </select>
      </label>

      {unit.symbol === 'custom' ? (
        <label className="ez-column-settings__field">
          Custom unit
          <input
            data-testid="numbers-custom-unit"
            value={unit.custom_unit ?? ''}
            maxLength={8}
            onChange={(event) => patchUnit({ custom_unit: event.target.value })}
          />
        </label>
      ) : null}

      <label className="ez-column-settings__field">
        Position
        <select
          data-testid="numbers-direction"
          value={unit.direction ?? 'right'}
          onChange={(event) => patchUnit({ direction: event.target.value as 'left' | 'right' })}
        >
          <option value="left">Before the number</option>
          <option value="right">After the number</option>
        </select>
      </label>

      <label className="ez-column-settings__field">
        Decimals
        <select
          data-testid="numbers-precision"
          value={String(unit.precision ?? -1)}
          onChange={(event) => patchUnit({ precision: Number(event.target.value) })}
        >
          {/* -1 is monday's "as typed": the stored value is rendered unrounded. */}
          <option value="-1">As typed</option>
          {[0, 1, 2, 3, 4, 5].map((precision) => (
            <option key={precision} value={String(precision)}>
              {precision}
            </option>
          ))}
        </select>
      </label>

      <label className="ez-column-settings__field">
        <input
          type="checkbox"
          data-testid="numbers-hide-footer"
          checked={draft.hide_footer ?? false}
          onChange={(event) => setDraft((current) => ({ ...current, hide_footer: event.target.checked }))}
        />
        Hide the group summary for this column
      </label>

      <footer className="ez-column-settings__footer">
        <button type="button" className="ez-column-settings__cancel" data-testid="settings-cancel" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="ez-column-settings__save" data-testid="settings-save">
          Apply
        </button>
      </footer>
    </form>
  );
}
